import { cn } from '@/lib/utils';
import { Inbox, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

// Reusable empty-state for any page that has nothing to show. Replaces
// the V1 PlaceholderPage skeletons — operators kept asking "is the page
// broken?" because the skeletons looked like a loading spinner. An
// explicit "0 datos" message is clearer.
//
// Usage:
//   <EmptyState
//     title="HISTORIAL"
//     description="..."
//     value="0"
//     unit="alertas"
//     hint="Las alertas aparecen acá cuando..."
//     icon={History}
//   />

interface Props {
  title: string;
  description: string;
  value?: string | number;
  unit?: string;
  hint?: string;
  icon?: LucideIcon;
  badge?: string;
  children?: ReactNode;
  className?: string;
}

export function EmptyState({
  title,
  description,
  value,
  unit,
  hint,
  icon: Icon = Inbox,
  badge,
  children,
  className,
}: Props) {
  return (
    <div className={cn('space-y-6', className)}>
      <header className="space-y-2">
        <div className="flex items-center gap-3">
          <h1
            className="font-brand text-3xl tracking-[0.25em] text-foreground"
            data-testid="page-title"
          >
            {title}
          </h1>
          {badge && (
            <span className="rounded-full border border-warning/40 bg-warning/10 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-warning">
              {badge}
            </span>
          )}
        </div>
        <p className="text-sm text-muted-foreground" data-testid="page-description">
          {description}
        </p>
      </header>

      <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border bg-card py-16 text-center">
        <Icon className="size-10 text-muted-foreground/60" strokeWidth={1.5} />
        {value !== undefined && (
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-5xl font-semibold text-muted-foreground">{value}</span>
            {unit && (
              <span className="font-mono text-sm uppercase tracking-wider text-muted-foreground">
                {unit}
              </span>
            )}
          </div>
        )}
        {hint && <p className="max-w-md px-6 text-sm text-muted-foreground">{hint}</p>}
        {children}
      </div>
    </div>
  );
}
